import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  place_commerce_order,
  pay_commerce_order,
  verify_commerce_payment,
  fetch_my_commerce_orders,
  fetch_commerce_order,
  cancel_commerce_shipment,
  fetch_vendor_shipments,
  transition_commerce_shipment,
  set_shipment_tracking,
  fetch_public_food_products,
  fetch_my_food_products,
  create_food_product,
  link_food_catalog_product,
  update_food_product,
  delete_food_product,
  create_food_variant,
  update_food_variant,
  delete_food_variant,
  save_food_details,
  fetch_food_details,
} from './api';
import type {
  CommerceShipmentStatus,
  CommerceShipmentIntent,
  PlaceCommerceOrderInput,
  VerifyCommercePaymentInput,
  SetShipmentTrackingInput,
  CreateFoodProductInput,
  LinkFoodCatalogProductInput,
  UpdateFoodProductInput,
  FoodDetailsRequest,
} from './types';
import type { CreateVariantInput, UpdateVariantInput } from '@/features/product-variant/types';

const MY_COMMERCE_ORDERS = ['my-commerce-orders'] as const;
const MY_FOOD_PRODUCTS = ['food-products'] as const;
const FOOD_DETAILS_KEY = ['module-details', 'food'] as const;

// ---- Owner: orders ----
export function use_my_commerce_orders() {
  return useQuery({ queryKey: MY_COMMERCE_ORDERS, queryFn: fetch_my_commerce_orders });
}

export function use_commerce_order(id?: string) {
  return useQuery({
    queryKey: ['commerce-order', id],
    queryFn: () => fetch_commerce_order(id as string),
    enabled: !!id,
  });
}

export function use_place_commerce_order() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input, files }: { input: PlaceCommerceOrderInput; files: File[] }) =>
      place_commerce_order(input, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_COMMERCE_ORDERS }),
  });
}

export function use_pay_commerce_order() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pay_commerce_order(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_COMMERCE_ORDERS }),
  });
}

export function use_verify_commerce_payment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: VerifyCommercePaymentInput }) => verify_commerce_payment(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_COMMERCE_ORDERS }),
  });
}

export function use_cancel_commerce_shipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ order_id, shipment_id, reason }: { order_id: string; shipment_id: string; reason?: string }) =>
      cancel_commerce_shipment(order_id, shipment_id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_COMMERCE_ORDERS }),
  });
}

// ---- Vendor: shipments inbox ----
export function use_vendor_shipments(status?: CommerceShipmentStatus) {
  return useQuery({
    queryKey: ['vendor-commerce-shipments', status ?? 'all'],
    queryFn: () => fetch_vendor_shipments(status),
  });
}

export function use_transition_commerce_shipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, intent, reason }: { id: string; intent: CommerceShipmentIntent; reason?: string }) =>
      transition_commerce_shipment(id, intent, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-commerce-shipments'] }),
  });
}

export function use_set_shipment_tracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SetShipmentTrackingInput }) => set_shipment_tracking(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-commerce-shipments'] }),
  });
}

// ---- Food catalog ----
/** Public catalog for the owner-side vendor detail page (available products only). */
export function use_public_food_products(vendor_id?: string) {
  return useQuery({
    queryKey: ['public-food-products', vendor_id],
    queryFn: () => fetch_public_food_products(vendor_id as string),
    enabled: !!vendor_id,
  });
}

export function use_my_food_products() {
  return useQuery({ queryKey: MY_FOOD_PRODUCTS, queryFn: fetch_my_food_products });
}

function useInvalidatingFoodProductMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: MY_FOOD_PRODUCTS }) });
}

export const use_create_food_product = () =>
  useInvalidatingFoodProductMutation((i: CreateFoodProductInput) => create_food_product(i));
export const use_link_food_catalog_product = () =>
  useInvalidatingFoodProductMutation((i: LinkFoodCatalogProductInput) => link_food_catalog_product(i));
export const use_update_food_product = () =>
  useInvalidatingFoodProductMutation(({ id, input }: { id: string; input: UpdateFoodProductInput }) =>
    update_food_product(id, input),
  );
export const use_delete_food_product = () => useInvalidatingFoodProductMutation((id: string) => delete_food_product(id));

// ---- Food catalog: variants ----
export const use_create_food_variant = () =>
  useInvalidatingFoodProductMutation(({ product_id, input }: { product_id: string; input: CreateVariantInput }) =>
    create_food_variant(product_id, input),
  );
export const use_update_food_variant = () =>
  useInvalidatingFoodProductMutation(({ id, input }: { id: string; input: UpdateVariantInput }) =>
    update_food_variant(id, input),
  );
export const use_delete_food_variant = () => useInvalidatingFoodProductMutation((id: string) => delete_food_variant(id));

// ---- Food onboarding module details ----
export function use_save_food_details() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FoodDetailsRequest) => save_food_details(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding', 'draft'] });
      qc.invalidateQueries({ queryKey: FOOD_DETAILS_KEY });
    },
  });
}

export function use_food_details(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FOOD_DETAILS_KEY,
    queryFn: fetch_food_details,
    staleTime: 0,
    retry: 0,
    enabled: options?.enabled ?? true,
  });
}
